import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  api,
  ApiError,
  type BurstResultWire,
  type CannedResponseWire,
  type CannedVariablesWire,
  type MediaAssetWire,
} from '../lib/api-client';

/**
 * Hooks de Respostas Prontas do Operador + biblioteca de mídia (Task 8, spec
 * 2026-07-16 §3.1/§3.2). Seguindo `feedback_gatear_fetch_nao_so_render`, as
 * QUERIES são gateadas por `enabled` (não só o render) — a Task 9 (UI) decide
 * quando a cap/tela justifica o fetch. `staleTime` de 5min nas listas: o
 * catálogo (respostas prontas + mídia) muda pouco, mirror `useSchedulingResources`.
 *
 * Retry-guard (B1 residual): 403 é negação DETERMINÍSTICA (cap opt-in negada
 * pelo Engine fail-closed — o gate-and-split de `QuickRepliesPage` a trata
 * inline com fallback ao legado); re-tentar só repetiria a negação. Demais
 * erros mantêm o retry default do React Query (3 tentativas).
 */
function noRetryOn403(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 403) return false;
  return failureCount < 3;
}

export function useCannedResponses(
  enabled = true,
  params?: { playbookId?: string; category?: string; q?: string },
): UseQueryResult<{ data: CannedResponseWire[] }, Error> {
  return useQuery({
    queryKey: ['canned-responses', params ?? null],
    queryFn: () => api.getCannedResponses(params),
    enabled,
    staleTime: 5 * 60_000,
    retry: noRetryOn403,
  });
}

export function useMediaAssets(
  enabled = true,
  params?: { kind?: 'image' | 'video' | 'audio' },
): UseQueryResult<{ data: MediaAssetWire[] }, Error> {
  return useQuery({
    queryKey: ['media-assets', params ?? null],
    queryFn: () => api.getMediaAssets(params),
    enabled,
    staleTime: 5 * 60_000,
    retry: noRetryOn403,
  });
}

export function useCreateCannedResponse(): UseMutationResult<
  CannedResponseWire,
  Error,
  Parameters<typeof api.createCannedResponse>[0]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createCannedResponse>[0]) =>
      api.createCannedResponse(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['canned-responses'] });
    },
  });
}

export function useUpdateCannedResponse(): UseMutationResult<
  CannedResponseWire,
  Error,
  { id: string; version: number; body: Parameters<typeof api.updateCannedResponse>[2] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: Parameters<typeof api.updateCannedResponse>[2];
    }) => api.updateCannedResponse(id, version, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['canned-responses'] });
    },
  });
}

export function useArchiveCannedResponse(): UseMutationResult<
  CannedResponseWire,
  Error,
  { id: string; version: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; version: number }) => api.archiveCannedResponse(p.id, p.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['canned-responses'] });
    },
  });
}

export function useSetCannedResponsePlaybooks(): UseMutationResult<
  CannedResponseWire,
  Error,
  { id: string; version: number; playbookIds: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; version: number; playbookIds: string[] }) =>
      api.setCannedResponsePlaybooks(p.id, p.version, p.playbookIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['canned-responses'] });
    },
  });
}

export function useUploadMediaAsset(): UseMutationResult<MediaAssetWire, Error, File> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadMediaAsset(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media-assets'] });
    },
  });
}

export function useArchiveMediaAsset(): UseMutationResult<
  MediaAssetWire,
  Error,
  { id: string; version: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; version: number }) => api.archiveMediaAsset(p.id, p.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media-assets'] });
    },
  });
}

/**
 * Respostas Prontas do Operador — uso na conversa (Task 6, S2, spec
 * 2026-07-16 §3.1). Variáveis camada 1 (7 chaves resolvidas pelo Engine a
 * partir do contexto da conversa; `null` = não-resolvível). Gate-the-fetch:
 * `enabled` E `conversationId` presente (nunca busca sem conversa ativa).
 * `staleTime` 60s (mais curto que o catálogo — o contexto da conversa muda).
 */
export function useCannedVariables(
  conversationId: string | undefined,
  enabled: boolean,
): UseQueryResult<CannedVariablesWire, Error> {
  return useQuery({
    queryKey: ['canned-variables', conversationId],
    queryFn: () => api.getCannedVariables(conversationId!),
    enabled: enabled && conversationId !== undefined,
    staleTime: 60_000,
    retry: noRetryOn403,
  });
}

/**
 * Burst de envio (Task 6, S2). `onSettled` (não `onSuccess`) invalida
 * `['messages', conversationId]` + `['conversations']` incondicionalmente —
 * mesmo em falha parcial (itens `failed`/`skipped`) o Engine pode ter
 * persistido mensagens reais que precisam aparecer no stream/lista.
 */
export function useSendBurst(
  conversationId: string | undefined,
): UseMutationResult<BurstResultWire, Error, Parameters<typeof api.sendMessageBurst>[1]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Parameters<typeof api.sendMessageBurst>[1]) => {
      // Guard da casa (mirror use-send-message.ts:80-82): mutate() sem conversa
      // ativa rejeita honesto — nunca manda "undefined" na URL do Engine.
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para burst');
      }
      return api.sendMessageBurst(conversationId, body);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
